/**
 * Encounter API client functions
 */

import { API_BASE_URL } from '../config/api';
import { Encounter, CreateEncounterRequest, UpdateEncounterRequest } from '../types/encounter';

// Custom error class that includes HTTP status
class EncounterAPIError extends Error {
  status: number;
  detail: string;

  constructor(status: number, detail: string) {
    super(detail);
    this.status = status;
    this.detail = detail;
    this.name = 'EncounterAPIError';
  }
}

const getAuthToken = (): string | null => {
  return localStorage.getItem('access_token') || localStorage.getItem('token');
};

const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
};

/**
 * Create a new encounter
 */
export const createEncounter = async (
  petId: string,
  encounterData: CreateEncounterRequest
): Promise<Encounter> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/pets/${petId}/encounters/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(encounterData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create encounter');
  }

  return response.json();
};

/**
 * Get an encounter by ID
 */
export const getEncounter = async (petId: string, encounterId: string): Promise<Encounter> => {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/pets/${petId}/encounters/${encounterId}`,
    {
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch encounter');
  }

  return response.json();
};

/**
 * Update an encounter
 */
export const updateEncounter = async (
  petId: string,
  encounterId: string,
  updateData: UpdateEncounterRequest
): Promise<Encounter> => {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/pets/${petId}/encounters/${encounterId}`,
    {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(updateData),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update encounter');
  }

  return response.json();
};

/**
 * Get all encounters for a pet
 */
export const listPetEncounters = async (petId: string): Promise<Encounter[]> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/pets/${petId}/encounters/`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch encounters');
  }

  return response.json();
};

/**
 * Get encounter for a specific appointment and pet combination.
 * Always use this instead of just appointment ID - appointments can have multiple pets!
 */
export const getEncounterForAppointmentAndPet = async (
  appointmentId: string,
  petId: string
): Promise<Encounter> => {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/appointments/${appointmentId}/pets/${petId}/encounter`,
    {
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new EncounterAPIError(
      response.status,
      error.detail || 'Failed to fetch encounter for appointment and pet'
    );
  }

  return response.json();
};

